import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import axios from "axios";
import { getAuthToken } from "../../../utils/getAuthToken";
import { getCached, setCached, invalidate, STORES, TTL } from "../../../utils/indexedDBCache";

// API base URL with fallback
const SERVER_DOMAIN = process.env.REACT_APP_SERVER_DOMAIN || 'http://localhost:8000';

// Debouncing for API calls to prevent spam
const debounceMap = new Map();
const DEBOUNCE_TIME = 3000; // 3 seconds - more aggressive for settings

const shouldDebounce = (key) => {
  const now = Date.now();
  const lastCall = debounceMap.get(key);
  
  if (lastCall && (now - lastCall) < DEBOUNCE_TIME) {
    return true; // Should debounce (skip this call)
  }
  
  debounceMap.set(key, now);
  return false; // Don't debounce (allow this call)
};

// Clears the module-level debounce map — call on logout so the next
// account's first fetch is never silently skipped as "too soon".
export const __resetInboxSettingsCaches = () => {
  debounceMap.clear();
};

// Consolidated async thunk for fetching both team members and teams data
export const fetchInboxTeamData = createAsyncThunk(
  "inboxSettings/fetchInboxTeamData",
  async (_, { rejectWithValue }) => {
    // Debounce check
    if (shouldDebounce('fetchInboxTeamData')) {
      console.log('🚫 Debounced fetchInboxTeamData call');
      return rejectWithValue("Debounced");
    }

    // Serve from IndexedDB cache when both entries are fresh.
    const [cachedMembers, cachedTeams] = await Promise.all([
      getCached(STORES.TEAM_DATA, 'teamMembers'),
      getCached(STORES.TEAM_DATA, 'teams'),
    ]);
    if (cachedMembers && cachedTeams) {
      return { teamMembers: cachedMembers, teams: cachedTeams };
    }

    // Get token from memory (not localStorage)
    const token = await getAuthToken();
    if (!token) {
      return rejectWithValue("No token found");
    }

    // Make both API calls in parallel, but resolve independently: a caller with
    // only one of Team.View/TeamMember.View gets a 403 on the other endpoint,
    // and that must not wipe out the data the caller IS permitted to see.
    const [teamMembersResult, teamsResult] = await Promise.allSettled([
      axios.get(`${SERVER_DOMAIN}/api/teammembers/assigned-count`, {
        headers: { Authorization: `Bearer ${token}` },
      }),
      axios.get(`${SERVER_DOMAIN}/api/teams/contact-count`, {
        headers: { Authorization: `Bearer ${token}` },
      }),
    ]);

    if (teamMembersResult.status === "rejected" && teamsResult.status === "rejected") {
      const error = teamMembersResult.reason;
      return rejectWithValue(error.response?.data?.message || error.message);
    }

    const teamMembers = teamMembersResult.status === "fulfilled" ? teamMembersResult.value.data : [];
    const teams = teamsResult.status === "fulfilled" ? teamsResult.value.data : [];

    // Fire-and-forget cache writes - only for the side that actually succeeded.
    if (teamMembersResult.status === "fulfilled") {
      setCached(STORES.TEAM_DATA, 'teamMembers', teamMembers, TTL.TEAM_DATA);
    }
    if (teamsResult.status === "fulfilled") {
      setCached(STORES.TEAM_DATA, 'teams', teams, TTL.TEAM_DATA);
    }

    return { teamMembers, teams };
  }
);

// Assign contact to a team member or team
export const assignContact = createAsyncThunk(
  "inboxSettings/assignContact",
  async ({ Id, contactId, type, Name }, { rejectWithValue, dispatch }) => {
    try {
      // Get token from memory (not localStorage)
      const token = await getAuthToken();
      if (!token) {
        throw new Error("No token found");
      }

      let response;
      
      // Use different endpoints based on type
      if (type === "team") {
        // POST /api/teams/assigncontact/:teamId
        response = await axios.post(
          `${SERVER_DOMAIN}/api/teams/assigncontact/${Id}`,
          { contactId },
          {
            headers: {
              Authorization: `Bearer ${token}`,
            },
          }
        );
      } else {
        // PUT /api/teammembers/assign/:id (default for team members)
        response = await axios.put(
          `${SERVER_DOMAIN}/api/teammembers/assign/${Id}`,
          { contactId },
          {
            headers: {
              Authorization: `Bearer ${token}`,
            },
          }
        );
      }

      // Assignment changes team counts — invalidate so next fetch is fresh.
      invalidate(STORES.TEAM_DATA, 'teamMembers');
      invalidate(STORES.TEAM_DATA, 'teams');

      // The acting user's own card must not depend on the socket round-trip: if the new assignee
      // is someone this user can't view, drop the card (e.g. a Closed row) right away, using the
      // same rule the live assignment event applies. Dynamic import + action type string because
      // socketSharedState / inboxRecentSlice both import this file (import cycle).
      try {
        const { shouldAllowRealtimeInboxCardUpdate } = await import("../../../utils/socketSharedState");
        const canStillView = shouldAllowRealtimeInboxCardUpdate({
          contactId,
          assignedTo: Id,
          assignedToType: type === "team" ? "team" : "member",
          lastMessage: { type: "assignment" },
        });
        if (!canStillView) dispatch({ type: "inbox/removeContactFromInbox", payload: { contactId } });
      } catch (visibilityError) {
        console.error("Failed to re-check inbox visibility after assignment:", visibilityError);
      }

      // Return response data with type and name for proper handling
      return {
        ...response.data,
        type,
        assignedToName: Name,
        assignedToId: Id,
      };
    } catch (error) {
      const data = error.response?.data;
      return rejectWithValue(
        data?.error || data?.message || error.message
      );
    }
  }
);

const inboxSettingsSlice = createSlice({
  name: "inboxSettings",
  initialState: {
    inboxCount: null,
    teamMembers: [],
    teams: [],
    loading: false,
    teamDataLoading: true,
    error: null,
    assignLoading: null,
    assignError: null,
    assignStatus: null,
  },
  reducers: {
    clearAssignState: (state) => {
      state.assignError = null;
      state.assignStatus = null;
    },
    setInboxCounts: (state, action) => {
      state.inboxCount = action.payload;
    },
    addContactToAssignment: (state, action) => {
      const { contactId, assignedToId, assignedToType } = action.payload;

      if (!contactId || !assignedToId || !assignedToType) {
        return; // Invalid payload, skip update
      }

      // Normalize IDs before comparing — assignedToId can arrive from a socket
      // payload as a different type/representation than the one already stored
      // on member._id/team._id (see updateAssignmentCount below, which already
      // does this); a strict === here silently no-ops the push, leaving the
      // contact "assigned" in the count but missing from the array.
      const assignedToIdStr = String(assignedToId);

      if (assignedToType === 'teammember') {
        const teamMember = state.teamMembers.find(
          member => String(member._id || member.id) === assignedToIdStr
        );
        if (teamMember) {
          if (!Array.isArray(teamMember.contactAssigned)) teamMember.contactAssigned = [];
          if (!teamMember.contactAssigned.some((id) => id.toString() === contactId.toString())) {
            teamMember.contactAssigned.push(contactId);
          }
          teamMember.assignedCount = typeof teamMember.assignedCount === 'number'
            ? teamMember.assignedCount + 1
            : 1;
        }
      } else if (assignedToType === 'team') {
        const team = state.teams.find(
          team => String(team._id || team.id) === assignedToIdStr
        );
        if (team) {
          if (!Array.isArray(team.AssignedQueue)) team.AssignedQueue = [];
          if (!team.AssignedQueue.some((id) => id.toString() === contactId.toString())) {
            team.AssignedQueue.push(contactId);
          }
          team.contactAssignedCount = typeof team.contactAssignedCount === 'number'
            ? team.contactAssignedCount + 1
            : 1;
        }
      }
    },
    removeContactFromAssignment: (state, action) => {
      const { contactId, assignedToId, assignedToType } = action.payload;
      
      if (!contactId || !assignedToId || !assignedToType) {
        return; // Invalid payload, skip update
      }

      // See addContactToAssignment above — normalize before comparing so a
      // type/representation mismatch doesn't silently no-op the removal.
      const assignedToIdStr = String(assignedToId);

      if (assignedToType === 'teammember') {
        // Remove contact from team member's contactAssigned array
        const teamMemberIndex = state.teamMembers.findIndex(
          member => String(member._id || member.id) === assignedToIdStr
        );

        if (teamMemberIndex !== -1) {
          const teamMember = state.teamMembers[teamMemberIndex];

          if (Array.isArray(teamMember.contactAssigned)) {
            teamMember.contactAssigned =
              teamMember.contactAssigned.filter(
                (id) => id.toString() !== contactId.toString()
              );
            // Update count to 0 if no contacts assigned, but keep the team member
            if (teamMember.contactAssigned.length === 0) {
              teamMember.contactAssigned = [];
            }
          } else {
            // Ensure contactAssigned is an empty array if it doesn't exist
            teamMember.contactAssigned = [];
          }
          // Update assignedCount
          if (typeof teamMember.assignedCount === 'number') {
            teamMember.assignedCount = Math.max(0, teamMember.assignedCount - 1);
          }
        }
      } else if (assignedToType === 'team') {
        // Remove contact from team's AssignedQueue array
        const teamIndex = state.teams.findIndex(
          team => String(team._id || team.id) === assignedToIdStr
        );
        
        if (teamIndex !== -1) {
          const team = state.teams[teamIndex];
          
          if (Array.isArray(team.AssignedQueue)) {
            team.AssignedQueue = 
              team.AssignedQueue.filter(
                (id) => id.toString() !== contactId.toString()
              );
            // Update count to 0 if no contacts assigned, but keep the team
            if (team.AssignedQueue.length === 0) {
              team.AssignedQueue = [];
            }
          } else {
            // Ensure AssignedQueue is an empty array if it doesn't exist
            team.AssignedQueue = [];
          }
          // Update contactAssignedCount
          if (typeof team.contactAssignedCount === 'number') {
            team.contactAssignedCount = Math.max(0, team.contactAssignedCount - 1);
          }
        }
      }
    },
    updateAssignmentCount: (state, action) => {
      const { assignedToId, assignedToType, increment } = action.payload;
      
      if (!assignedToId || assignedToType === undefined || increment === undefined) {
        return; // Invalid payload, skip update
      }

      // Normalize ID for comparison
      const normalizeId = (id) => {
        if (!id) return null;
        return String(id);
      };

      if (assignedToType === 'teammember' || assignedToType === 'member') {
        // Update team member count
        const teamMemberIndex = state.teamMembers.findIndex(
          member => {
            const memberId = normalizeId(member._id || member.id);
            const targetId = normalizeId(assignedToId);
            return memberId === targetId;
          }
        );
        
        if (teamMemberIndex !== -1) {
          const teamMember = state.teamMembers[teamMemberIndex];
          if (typeof teamMember.assignedCount === 'number') {
            teamMember.assignedCount = Math.max(0, teamMember.assignedCount + increment);
          } else {
            // Initialize if doesn't exist
            teamMember.assignedCount = Math.max(0, increment);
          }
        }
        // Bug fix (2026-09-12): this branch used to push a placeholder entry here
        // using the raw assignedToId AS the display `name` when the member isn't
        // loaded locally yet (a real timing race, not necessarily bad data) — that
        // placeholder showed as a raw ID string in the assign dropdown/sidebar, and
        // nothing ever went back to replace it with the real name. Safer to just
        // skip the optimistic count bump for this one member (their count is
        // briefly stale until the next real fetchInboxTeamData) than to corrupt the
        // visible list with an ID-as-name row.
      } else if (assignedToType === 'team') {
        // Update team count
        const teamIndex = state.teams.findIndex(
          team => {
            const teamId = normalizeId(team._id || team.id);
            const targetId = normalizeId(assignedToId);
            return teamId === targetId;
          }
        );
        
        if (teamIndex !== -1) {
          const team = state.teams[teamIndex];
          if (typeof team.contactAssignedCount === 'number') {
            team.contactAssignedCount = Math.max(0, team.contactAssignedCount + increment);
          } else {
            // Initialize if doesn't exist
            team.contactAssignedCount = Math.max(0, increment);
          }
        }
        // Same fix as the teammember branch above — no placeholder-with-ID-as-name.
      }
    },
  },
  extraReducers: (builder) => {
    builder
      // Cases for consolidated fetchInboxTeamData
      .addCase(fetchInboxTeamData.pending, (state) => {
        state.loading = true;
        state.teamDataLoading = true;
        state.error = null;
      })
      .addCase(fetchInboxTeamData.fulfilled, (state, action) => {
        state.loading = false;
        state.teamDataLoading = false;
        const { teamMembers, teams } = action.payload;
        
        if (teamMembers.message === "Permission denied") {
          state.teamMembers = [];
        } else {
          // Normalize data: ensure id field exists (map from _id if needed)
          state.teamMembers = Array.isArray(teamMembers)
            ? teamMembers.map(member => ({
                ...member,
                id: member.id || member._id,
              }))
            : [];
        }
        
        if (teams.message === "Permission denied") {
          state.teams = [];
        } else {
          // Normalize data: ensure id field exists (map from _id if needed)
          state.teams = Array.isArray(teams)
            ? teams.map(team => ({
                ...team,
                id: team.id || team._id,
              }))
            : [];
        }
      })
      .addCase(fetchInboxTeamData.rejected, (state, action) => {
        state.loading = false;
        state.teamDataLoading = false;
        if (action.payload !== "Debounced") {
          state.teamMembers = [];
          state.teams = [];
          state.error = action.payload;
        }
      })

      // Assign a contact
      .addCase(assignContact.pending, (state) => {
        state.assignLoading = true;
        state.assignError = null;
        state.assignStatus = null;
      })
      .addCase(assignContact.fulfilled, (state, action) => {
        state.assignLoading = false;
        state.assignStatus = "successful";
        // Invalidate stale contact cache so the next open reflects new assignment
        const contactId = action.meta.arg?.contactId;
        if (contactId) {
          invalidate(STORES.CONTACTS, String(contactId));
        }
      })
      .addCase(assignContact.rejected, (state, action) => {
        console.log(action.payload);
        state.assignLoading = false;
        state.assignStatus = "failed";
        state.assignError = action.payload || "Failed to assign contact";
      });
  },
});
export const { clearAssignState, setInboxCounts, addContactToAssignment, removeContactFromAssignment, updateAssignmentCount } = inboxSettingsSlice.actions;

export default inboxSettingsSlice.reducer;
